import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box } from '@ltvco/refresh-lib/theme';
import { ReportGeneratingScreen } from '@ltvco/refresh-lib/v2';
import { useState } from 'react';

import { adaptCourtCaseReport } from 'routes/Dashboard/CourtCases/v2/adapters/adaptCourtCaseReport';
import {
  StyledTab,
  StyledTabsNavigation,
} from 'routes/Dashboard/CourtCases/v2/CasesSearch/CaseDetails/components';
import { CourtCaseDetailsHeader } from 'routes/Dashboard/CourtCases/v2/CasesSearch/CaseDetails/CourtCaseDetailsHeader';
import { DocketEntries } from 'routes/Dashboard/CourtCases/v2/CasesSearch/CaseDetails/DocketEntries';
import { PartiesAttorneys } from 'routes/Dashboard/CourtCases/v2/CasesSearch/CaseDetails/PartiesAttorneys';
import { JudgeDetails } from 'routes/Dashboard/CourtCases/v2/JudgeSearch';
import {
  CourtCaseDisclaimer,
  InlineLoading,
  ErrorStateContainer,
} from 'routes/Dashboard/CourtCases/v2/shared';

import type { ReportRouteProps } from './types';

interface CourtCaseReportProps extends ReportRouteProps {}

export function CourtCaseReportPage({
  permalink,
  firstReportScreen,
}: CourtCaseReportProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [docketEntriesCount, setDocketEntriesCount] = useState(0);
  const queryResult = useReport(permalink);

  const onTabChanged = (newTab: number) => {
    setActiveTab(newTab);
  };

  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="court-case"
        ready={!queryResult.isLoading && Boolean(queryResult.data)}
        onComplete={firstReportScreen.onComplete}
      />
    );
  }

  if (queryResult.isError) {
    return (
      <ErrorStateContainer>
        <p>Court case report data is unavailable.</p>
      </ErrorStateContainer>
    );
  }

  if (queryResult.isLoading || !queryResult.data) {
    return <InlineLoading />;
  }

  const entity = queryResult.data.entities?.court_case;
  if (!entity?.docket || Object.keys(entity.docket).length === 0) {
    return (
      <ErrorStateContainer>
        <p>Court case report data is unavailable.</p>
      </ErrorStateContainer>
    );
  }

  const viewModel = adaptCourtCaseReport(entity);
  const { docket, court, entries, partiesAttorneysResult, judge } = viewModel;

  const docketsTabCount = Math.max(docketEntriesCount, entries.length);

  const tabs = [
    {
      label: `Dockets (${docketsTabCount})`,
      content: (
        <DocketEntries
          pacerCaseId={docket.pacer_case_id}
          courtId={docket.court_id}
          pacerCourtId={court?.pacer_court_id}
          docketId={String(docket.id)}
          onCountChange={setDocketEntriesCount}
          entries={entries}
        />
      ),
    },
    {
      label: `Parties & Attorneys (${partiesAttorneysResult.partiesCount})`,
      content: (
        <PartiesAttorneys partiesAttorneysResult={partiesAttorneysResult} />
      ),
    },
    ...(judge
      ? [
          {
            label: 'Judge',
            content: (
              <JudgeDetails
                judgeId={String(judge.person.id)}
                person={judge.person}
                positions={judge.positions}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Box>
      <CourtCaseDetailsHeader selectedDocket={docket} courtInfo={court} />
      <StyledTabsNavigation
        externalActiveTab={activeTab}
        onTabChanged={onTabChanged}
        tab={StyledTab}
        tabs={tabs}
      />
      <CourtCaseDisclaimer />
    </Box>
  );
}
